/**
 * Route-access helpers over the shared role policy (shared/accessPolicy.ts).
 * Role sets live in the shared tables only; this module layers the
 * frontend-specific concerns on top: feature-flag gates per route, team-policy
 * hiding, and role default redirects. For non-route checks, use
 * `roleHasCapability` from @shared/accessPolicy directly.
 */

import {
  INTERNAL_ROLES,
  ROUTE_DELEGATIONS,
  userHasCapability,
} from '@shared/accessPolicy';
import { FEATURE_FLAGS } from '@shared/featureFlags';
import type { TeamPolicy } from '@shared/teamPolicy';
import type { RoleId } from '@/config/roles';
import {
  DEFAULT_REDIRECTS,
  ROLES,
  ROUTE_ACCESS,
  SUPER_ADMIN_FEATURE_BYPASS,
} from '@/config/roles';
import {
  hasEffectiveFeatureAccess,
  isRouteHiddenByTeamPolicy,
} from '@/utils/teamPolicyAccess';

interface FeatureFlagUser {
  role: RoleId;
  featureFlags?: Map<string, boolean> | Readonly<Record<string, boolean>>;
  teamPolicy?: TeamPolicy | null;
}

/**
 * Collapse a pathname to the form the route tables are keyed on: no trailing
 * slash (root excepted).
 *
 * React Router matches `/tasks/` and `/tasks` to the same route, but an exact
 * table lookup does not — the slashed form missed both `ROUTE_ACCESS` (so the
 * gate fell through to allow-on-miss and let staff onto a student-only page)
 * and the app-shell set in `App.tsx` (so the page rendered with no navigation).
 * Every pathname → table lookup normalizes first.
 */
export const normalizeRoutePath = (pathname: string): string =>
  pathname.length > 1 ? pathname.replace(/\/+$/, '') || '/' : pathname;

/**
 * Checks if a user role has access to a specific route
 */
export const hasRouteAccess = (
  userRole: RoleId | string | null | undefined,
  route: string,
  user: FeatureFlagUser | null = null
): boolean => {
  if (!userRole || !route) return false;

  const path = normalizeRoutePath(route);
  const allowedRoles = ROUTE_ACCESS[path];
  // Several internal detail routes intentionally rely on page/server gates and
  // therefore have no exact table row. Preserve that internal convention, but
  // never let an external or unknown role turn a missing key into access.
  if (!allowedRoles) {
    return (INTERNAL_ROLES as readonly string[]).includes(userRole);
  }

  if (isRouteHiddenByTeamPolicy(path, user?.teamPolicy)) {
    return false;
  }

  // Check role access or an explicitly mapped per-user capability delegation.
  // With no user object this remains an exact route × role lookup, which keeps
  // the policy sweep and server endpoint-gate tests role-pure.
  const hasRoleAccess = (allowedRoles as readonly string[]).includes(userRole);
  const delegatedCapability = ROUTE_DELEGATIONS[path];
  const hasDelegatedAccess =
    !!user &&
    delegatedCapability !== undefined &&
    userHasCapability(user, delegatedCapability);
  if (!hasRoleAccess && !hasDelegatedAccess) return false;

  // Check the remaining route-specific feature flag. Super admins bypass the
  // gate for routes listed in SUPER_ADMIN_FEATURE_BYPASS.
  const routeFeatureMap: Record<string, string> = {
    '/admin/feature-flags': FEATURE_FLAGS.ADMIN_FEATURE_MANAGEMENT.id,
  };

  const requiredFeature = routeFeatureMap[path];
  if (requiredFeature && user) {
    if (
      userRole === ROLES.SUPER_ADMIN &&
      SUPER_ADMIN_FEATURE_BYPASS.has(path)
    ) {
      return true;
    }
    return hasEffectiveFeatureAccess(user, requiredFeature);
  }

  return hasRoleAccess || hasDelegatedAccess;
};

/**
 * Gets the default redirect path based on user role
 */
export const getDefaultRedirectPath = (
  userRole: RoleId,
  user: FeatureFlagUser | null = null
): string => {
  if (
    userRole === ROLES.TEACHER &&
    isRouteHiddenByTeamPolicy(
      DEFAULT_REDIRECTS[ROLES.TEACHER],
      user?.teamPolicy
    )
  ) {
    return '/teacher/students';
  }
  return DEFAULT_REDIRECTS[userRole] || '/practice';
};

/**
 * Validates if a role is valid
 */
export const isValidRole = (role: string): role is RoleId => {
  return (Object.values(ROLES) as string[]).includes(role);
};
